import { compose } from 'redux';
import classNames from 'classnames';
import { Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useDropzone } from 'react-dropzone';
import { connect, useSelector } from 'react-redux';
import withStyles from '@material-ui/styles/withStyles';
import { useEffect, useCallback, useMemo, useState } from 'react';

import { BasePage } from 'altus-ui-components';

import {
  onLoad,
  deleteFile,
  updateFile,
  uploadFiles,
  downloadFile,
} from 'features/wells/wellboreFiles.actions';

import {
  getSummarizedDataStateFromState,
  getAvailableFileCategoriesFromState,
  getAllOrganizationsFromState,
  getCurrentUserFromState,
} from 'app/app.selectors';

import { downloadFile as downloadProjectFile } from 'features/projects/documents/documents.actions';

import { setFile } from 'app/app.actions';
import { useHeader } from 'app/hooks/useHeader';
import { Fab } from 'app/components/withTooltip';
import { EMPTY_SET, MAX_UPLOAD_FILE_SIZE } from 'app/app.constants';
import UploadDocumentModal from 'app/components/UploadDocumentModal';
import WellboreFilesTable from 'features/wells/components/WellboreFilesTable';
import { WELLBORE_FILE_ACTIONS } from 'features/wells/wellboreFiles.constants';
import { getWellboreFilesFromState } from 'features/wells/wellboreFiles.selectors';
import { DOCUMENTS_ACTIONS } from 'features/projects/documents/documents.constants';
import DocumentsCategoryFilter from 'features/projects/documents/newDocumentsContainer/components/DocumentsCategoryFilter';
import { ALTUS_ORGANIZATION_CODE } from 'app/hooks/useIsInternalCategoryToDisplay/constants';

const WellboreFilesContainer = ({
  classes,
  dataState,
  wellboreId,
  fieldId,
  breadcrumb,
  dispatchOnLoad,
  dispatchDropFiles,
  dispatchDeleteFile,
  dispatchUpdateFile,
  dispatchUploadFiles,
  dispatchDownloadFile,
  dispatchDownloadProjectFile,
}) => {
  useHeader({ subTitle: breadcrumb });

  const user = useSelector(getCurrentUserFromState);
  const availableOrganizations = useSelector(getAllOrganizationsFromState);

  useEffect(() => {
    dispatchOnLoad(wellboreId, fieldId);
  }, [wellboreId, dispatchOnLoad, fieldId]);

  const [filter, setFilter] = useState(EMPTY_SET);
  const [filterApplied, setFilterApplied] = useState(false);

  const documents = useSelector(getWellboreFilesFromState);
  const documentCategories = useSelector(getAvailableFileCategoriesFromState);  

  const filteredDocuments = useMemo(() => {
    if (filter.isEmpty()) return documents;

    return documents.filter((document) => {
      const documentCategory = documentCategories.get(document.get('category'));

      return filter.includes(documentCategory);
    });
  }, [filter, documents, documentCategories]);

  useEffect(() => {
    // clear filter if deleting the last document to prevent a filter being active when no documents match it
    if (filteredDocuments.isEmpty()) {
      setFilter(EMPTY_SET);
    }
  }, [filteredDocuments]);

  const downloadFile = useCallback(
    (wellboreFileId, wellboreFile) =>
      dispatchDownloadFile(wellboreId, wellboreFileId, wellboreFile, fieldId),
    [wellboreId, dispatchDownloadFile, fieldId],
  );

  const downloadProjectFile = useCallback(
    (projectId, fileId, file) =>
      dispatchDownloadProjectFile(projectId, fileId, file),
    [dispatchDownloadProjectFile],
  );

  const deleteFile = useCallback(
    (wellboreFileId, file) => dispatchDeleteFile(wellboreId, wellboreFileId, fieldId, file),
    [wellboreId, dispatchDeleteFile, fieldId],
  );

  const updateFile = useCallback(
    (wellboreFile) =>
      dispatchUpdateFile(
        wellboreId,
        wellboreFile.fileId,
        wellboreFile,
        fieldId,
      ),
    [wellboreId, dispatchUpdateFile, fieldId],
  );

  const uploadFiles = useCallback(
    (wellboreFiles) => dispatchUploadFiles(wellboreId, fieldId)(wellboreFiles),
    [wellboreId, dispatchUploadFiles, fieldId],
  );

  const onDrop = useCallback(
    (wellboreFiles) => dispatchDropFiles(wellboreFiles),
    [dispatchDropFiles],
  );

  const clearFilters = () => {
    setFilter(EMPTY_SET);
    setFilterApplied(false);
  };

  const { isDragActive, getRootProps, getInputProps, open, fileRejections } =
    useDropzone({
      onDrop,
      noClick: true,
      noKeyboard: true,
      maxSize: MAX_UPLOAD_FILE_SIZE,
    });

  const checkIfUserOrgIsAltus = (user, organizations) => {
    const currentOrg = Array.from(organizations).find(
      (org) => org.get('id') === user.get('organizationId'),
    );

    if (currentOrg?.get('code') === ALTUS_ORGANIZATION_CODE) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <BasePage
      dataState={dataState}
      classes={{
        children: classes.basePageChildren,
        root: classNames({
          [classes.dragOver]: isDragActive,
        }),
      }}
    >
      <Grid item container xs spacing={4} {...getRootProps()}>
        <Grid item xs={2}>
          <DocumentsCategoryFilter
            filter={filter}
            clearFilters={clearFilters}
            setFilter={setFilter}
            documents={documents}
            documentCategories={documentCategories}
            setFilterApplied={setFilterApplied}
            isAltus={checkIfUserOrgIsAltus(user, availableOrganizations)}
          />
        </Grid>
        <Grid item xs={8}>
          <WellboreFilesTable
            updateFile={updateFile}
            deleteFile={deleteFile}
            downloadFile={downloadFile}
            downloadProjectFile={downloadProjectFile}
            documents={filteredDocuments}
            documentCategoriesById={documentCategories}
            noItemsMessage="Upload one or more documents simultaneously by using the + button or drag them here"
            filterApplied={filterApplied}
          />
        </Grid>
        <Grid item xs={2}>
          <UploadDocumentModal
            uploadFiles={uploadFiles}
            rejectedFiles={fileRejections}
            availableFileCategories={documentCategories.valueSeq()}
            dialogContentText="Set meta data and upload files to project"
          />
          <Fab
            onClick={open}
            color="primary"
            title="Upload documents"
            className={classes.create}
          >
            <AddIcon />
          </Fab>
        </Grid>
      </Grid>
      <input {...getInputProps()} />
    </BasePage>
  );
};

const mapStateToProps = (state) => ({
  dataState: getSummarizedDataStateFromState(
    state,
    WELLBORE_FILE_ACTIONS.DOWNLOAD_WELLBORE_FILE,
    WELLBORE_FILE_ACTIONS.UPLOAD_WELLBORE_FILE,
    WELLBORE_FILE_ACTIONS.UPDATE_WELLBORE_FILE,
    WELLBORE_FILE_ACTIONS.DELETE_WELLBORE_FILE,
    WELLBORE_FILE_ACTIONS.WELLBORE_FILES_PAGE_LOADED,
    DOCUMENTS_ACTIONS.DOWNLOAD_PROJECT_FILE,
  ),
});

const mapDispatchToProps = {
  dispatchOnLoad: onLoad,
  dispatchDropFiles: setFile,
  dispatchDeleteFile: deleteFile,
  dispatchUpdateFile: updateFile,
  dispatchUploadFiles: uploadFiles,
  dispatchDownloadFile: downloadFile,
  dispatchDownloadProjectFile: downloadProjectFile,
};

const styles = (theme) => ({
  create: {
    zIndex: 2,
    position: 'absolute',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
  },
  dragOver: {
    opacity: '0.3',
    border: '5px gray dashed',
  },
  documentList: {
    height: '100%',
  },
  basePageChildren: {
    flexDirection: 'row',
    paddingTop: theme.spacing(2.25),
  },
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles),
)(WellboreFilesContainer);
